import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { ShippingForm } from "./ShippingForm";

interface ShippingMethod {
 _id: string;
 name: string;
 description?: string;
 type: "standard" | "express" | "pickup" | "digital";
 isActive: boolean;
 basePrice: number;
 pricePerKg?: number;
 freeShippingThreshold?: number;
 estimatedDays: {
  min: number;
  max: number;
 };
 createdAt: string;
}

export const Shipping: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [methods, setMethods] = useState<ShippingMethod[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showForm, setShowForm] = useState(false);
 const [editingId, setEditingId] = useState<string | null>(null);
 const [filterType, setFilterType] = useState<string>("");
 const [filterActive, setFilterActive] = useState<string>("");
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading, setBulkActionLoading] = useState(false);

 useEffect(() => {
  fetchMethods();
 }, [filterType, filterActive]);

 const fetchMethods = async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams();
   if (filterType) params.append("type", filterType);
   if (filterActive) params.append("isActive", filterActive);
   
   const response = await api.get(`/shipping/admin?${params.toString()}`);
   setMethods(response.data.methods || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleDelete = async (methodId: string) => {
  if (!window.confirm(t("admin.shipping.confirmDelete"))) {
   return;
  }

  try {
   await api.delete(`/shipping/admin/${methodId}`);
   fetchMethods();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.delete"));
  }
 };

 const handleEdit = (method: ShippingMethod) => {
  setEditingId(method._id);
  setShowForm(true);
 };

 const handleFormClose = () => {
  setShowForm(false);
  setEditingId(null);
  fetchMethods();
 };

 const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
   standard: t("admin.shipping.typeStandard"),
   express: t("admin.shipping.typeExpress"),
   pickup: t("admin.shipping.typePickup"),
   digital: t("admin.shipping.typeDigital"),
  };
  return labels[type] || type;
 };

 const handleSelectAll = (checked: boolean) => {
  if (checked) {
   setSelectedIds(new Set(methods.map((m) => m._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };

 const handleBulkDelete = async () => {
  if (selectedIds.size === 0) return;
  if (!window.confirm(t("admin.shipping.confirmBulkDelete", { count: selectedIds.size }))) {
   return;
  }

  try {
   setBulkActionLoading(true);
   setError(null);
   await api.post("/shipping/admin/bulk/delete", { ids: Array.from(selectedIds) });
   setSelectedIds(new Set());
   fetchMethods();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.delete"));
  } finally {
   setBulkActionLoading(false);
  }
 };

 const handleBulkStatusChange = async (isActive: boolean) => {
  if (selectedIds.size === 0) return;

  try {
   setBulkActionLoading(true);
   setError(null);
   await api.post("/shipping/admin/bulk/status", { ids: Array.from(selectedIds), isActive });
   setSelectedIds(new Set());
   fetchMethods();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.shipping.errors.updateStatus"));
  } finally {
   setBulkActionLoading(false);
  }
 };

 if (loading && methods.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.shipping.title")}</Heading>
     <Button onClick={() => setShowForm(true)}>{t("admin.shipping.addMethod")}</Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    <ShippingForm
     isOpen={showForm}
     methodId={editingId}
     onClose={handleFormClose}
     onSuccess={handleFormClose}
    />

    <Card padding="lg">
     <Stack spacing="md">
      <div className="flex gap-4 items-center">
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.shipping.filterByType")}
        </label>
        <select
         value={filterType}
         onChange={(e) => setFilterType(e.target.value)}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
         <option value="">{t("admin.shipping.allTypes")}</option>
         <option value="standard">{t("admin.shipping.typeStandard")}</option>
         <option value="express">{t("admin.shipping.typeExpress")}</option>
         <option value="pickup">{t("admin.shipping.typePickup")}</option>
         <option value="digital">{t("admin.shipping.typeDigital")}</option>
        </select>
       </div>
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.shipping.filterByStatus")}
        </label>
        <select
         value={filterActive}
         onChange={(e) => setFilterActive(e.target.value)}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
         <option value="">{t("admin.shipping.allStatus")}</option>
         <option value="true">{t("admin.active")}</option>
         <option value="false">{t("admin.inactive")}</option>
        </select>
       </div>
      </div>

      <Heading size="lg">{t("admin.shipping.methods")}</Heading>
      {selectedIds.size > 0 && (
       <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 ">
        <div className="text-sm font-medium text-blue-900">
         {t("admin.shipping.selectedCount", { count: selectedIds.size })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkStatusChange(true)}
          disabled={bulkActionLoading}
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.shipping.activate")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkStatusChange(false)}
          disabled={bulkActionLoading}
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.shipping.deactivate")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="text-red-600 hover:text-red-700"
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.shipping.deleteSelected")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={() => setSelectedIds(new Set())}
          disabled={bulkActionLoading}
         >
          {t("admin.shipping.clearSelection")}
         </Button>
        </div>
       </div>
      )}

      {methods.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.shipping.noMethods")}
       </div>
      ) : (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <input
             type="checkbox"
             checked={selectedIds.size > 0 && selectedIds.size === methods.length}
             onChange={(e) => handleSelectAll(e.target.checked)}
             className="rounded border-gray-300"
            />
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.shipping.name")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.shipping.type")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.shipping.basePrice")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.shipping.estimatedDays")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.status")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.actions")}
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {methods.map((method) => (
           <tr key={method._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <input
              type="checkbox"
              checked={selectedIds.has(method._id)}
              onChange={(e) => handleSelectOne(method._id, e.target.checked)}
              className="rounded border-gray-300"
             />
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <button
              onClick={() => handleEdit(method)}
              className="text-left hover:opacity-80 transition-opacity cursor-pointer"
              type="button"
             >
              <div className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">
               {method.name}
              </div>
              {method.description && (
               <div className="text-sm text-gray-500">
                {method.description}
               </div>
              )}
             </button>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800">
              {getTypeLabel(method.type)}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             ${method.basePrice.toFixed(2)}
             {method.pricePerKg && ` + $${method.pricePerKg.toFixed(2)}/kg`}
             {method.freeShippingThreshold && (
              <div className="text-xs text-gray-400">
               {t("admin.shipping.freeOver", { amount: method.freeShippingThreshold.toFixed(2) })}
              </div>
             )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {t("admin.shipping.estimatedDaysRange", { min: method.estimatedDays.min, max: method.estimatedDays.max })}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold ${
               method.isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
              }`}
             >
              {method.isActive ? t("admin.active") : t("admin.inactive")}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
             <div className="flex gap-2">
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleEdit(method)}
              >
               {t("admin.edit")}
              </Button>
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleDelete(method._id)}
               className="text-red-600 hover:text-red-700"
              >
               {t("admin.delete")}
              </Button>
             </div>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Shipping;

